import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { injectTSDI as tsdi } from '../../../../core/tsdi';
import { Api } from '../../../../core/api';
import type { ActivityOverviewSupportDto } from '../../../../core/api/FitnessActivityClient';
import { ActivityType } from '../../../../core/api/FitnessActivityClient';
import {
    ActivityFilterItem,
    ActivityFilterSubType,
    UNKNOWN_SUBTYPE
} from './activityFilter.utils';

export interface DayActivityData {
    date: Date;
    data: ActivityOverviewSupportDto | null;
    error: string | null;
    loading: boolean;
}

export type SelectedSubtypesByCategory = {
    dailySteps: Set<ActivityFilterSubType>;
    facilityVisits: Set<ActivityFilterSubType>;
    workouts: Set<ActivityFilterSubType>;
};

export const buildSelectedSubtypesByCategory = (
    selectedActivities: ActivityFilterItem[]
): SelectedSubtypesByCategory => {
    const dailySteps = new Set<ActivityFilterSubType>();
    const facilityVisits = new Set<ActivityFilterSubType>();
    const workouts = new Set<ActivityFilterSubType>();

    selectedActivities.forEach((activity) => {
        if (activity.category === ActivityType.DAILY_STEPS) {
            dailySteps.add(activity.subType);
        } else if (activity.category === ActivityType.FACILITY_VISIT) {
            facilityVisits.add(activity.subType);
        } else if (activity.category === ActivityType.WORKOUT) {
            workouts.add(activity.subType);
        }
    });

    return { dailySteps, facilityVisits, workouts };
};

export function filterDataByActivities(
    data: ActivityOverviewSupportDto | null,
    selectedActivities: ActivityFilterItem[],
    selectedSubtypes?: SelectedSubtypesByCategory
): ActivityOverviewSupportDto | null {
    if (!data || selectedActivities.length === 0) {
        return data;
    }

    const subtypes =
        selectedSubtypes ?? buildSelectedSubtypesByCategory(selectedActivities);

    const hasDailySteps = subtypes.dailySteps.size > 0;
    const hasFacilityVisits = subtypes.facilityVisits.size > 0;
    const hasWorkouts = subtypes.workouts.size > 0;

    let filteredDailySteps = data.dailySteps;
    if (hasDailySteps) {
        filteredDailySteps = data.dailySteps.filter((step) =>
            subtypes.dailySteps.has(step.fitnessApp ?? UNKNOWN_SUBTYPE)
        );
    } else {
        filteredDailySteps = [];
    }

    let filteredFacilityVisits = data.facilityVisits;
    if (hasFacilityVisits) {
        filteredFacilityVisits = data.facilityVisits.filter((visit) =>
            subtypes.facilityVisits.has(visit.visitType ?? UNKNOWN_SUBTYPE)
        );
    } else {
        filteredFacilityVisits = [];
    }

    let filteredWorkouts = data.workouts;
    if (hasWorkouts) {
        filteredWorkouts = data.workouts.filter((workout) =>
            subtypes.workouts.has(workout.workoutType ?? UNKNOWN_SUBTYPE)
        );
    } else {
        filteredWorkouts = [];
    }

    const filteredActivityPointsChanges = hasWorkouts
        ? data.activityPointsChanges
        : [];

    const filteredData: ActivityOverviewSupportDto = {
        ...data,
        dailySteps: filteredDailySteps,
        activityPointsChanges: filteredActivityPointsChanges,
        workouts: filteredWorkouts,
        facilityVisits: filteredFacilityVisits
    };

    return filteredData;
}

function formatDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function useActivityData(
    userId: string,
    selectedDates: Date[],
    selectedActivities: ActivityFilterItem[] = []
) {
    const [dayDataMap, setDayDataMap] = useState<Map<string, DayActivityData>>(
        new Map()
    );
    const fetchedDatesRef = useRef<Set<string>>(new Set());

    const fetchDataForDate = useCallback(
        async (date: Date) => {
            const dateKey = formatDateKey(date);

            fetchedDatesRef.current.add(dateKey);

            setDayDataMap((prev) =>
                new Map(prev).set(dateKey, {
                    date,
                    data: null,
                    error: null,
                    loading: true
                })
            );

            try {
                const api = tsdi(Api);
                const day = formatDateKey(date);

                const response =
                    await api.fitnessActivityClient.admin.fraudMonitorSupportControllerGetActivities(
                        {
                            userId,
                            day
                        }
                    );

                setDayDataMap((prev) =>
                    new Map(prev).set(dateKey, {
                        date,
                        data: response.data,
                        error: null,
                        loading: false
                    })
                );
            } catch (e: any) {
                setDayDataMap((prev) =>
                    new Map(prev).set(dateKey, {
                        date,
                        data: null,
                        error: e?.message ?? 'Failed to fetch activity data',
                        loading: false
                    })
                );
            }
        },
        [userId]
    );

    useEffect(() => {
        selectedDates.forEach((date) => {
            const dateKey = formatDateKey(date);
            if (!fetchedDatesRef.current.has(dateKey)) {
                fetchDataForDate(date);
            }
        });

        const dateKeys = new Set(selectedDates.map((d) => formatDateKey(d)));
        setDayDataMap((prev) => {
            const newMap = new Map(prev);
            for (const key of newMap.keys()) {
                if (!dateKeys.has(key)) {
                    newMap.delete(key);
                    fetchedDatesRef.current.delete(key);
                }
            }
            return newMap;
        });
    }, [selectedDates, fetchDataForDate]);

    const rawDayDataArray = useMemo<DayActivityData[]>(() => {
        const values = Array.from(dayDataMap.values()) as DayActivityData[];
        return values.sort((a, b) => a.date.getTime() - b.date.getTime());
    }, [dayDataMap]);

    const selectedSubtypes = useMemo(
        () => buildSelectedSubtypesByCategory(selectedActivities),
        [selectedActivities]
    );

    const dayDataArray = useMemo<DayActivityData[]>(
        () =>
            rawDayDataArray.map((dayData: DayActivityData) => ({
                ...dayData,
                data: filterDataByActivities(
                    dayData.data,
                    selectedActivities,
                    selectedSubtypes
                )
            })),
        [rawDayDataArray, selectedActivities, selectedSubtypes]
    );

    const isLoading = rawDayDataArray.some((d: DayActivityData) => d.loading);
    const hasError = rawDayDataArray.some(
        (d: DayActivityData) => d.error !== null
    );

    return {
        dayDataArray,
        rawDayDataArray,
        isLoading,
        hasError,
        refetch: () => selectedDates.forEach(fetchDataForDate)
    };
}
